import React, { createContext, MutableRefObject, useContext } from 'react';

import { noop } from 'lodash';
import { ELumaBotFlowType, ILumaBotFlowQuestion, ILumaBotFlowTemplate } from 'ui-model';

import { Setting } from '@lib/resources/types';

import { TMessageWrapper } from './components/MessageWrapper';

export interface LumaBotContextProps {
  settings: Setting['lumabot'];
  flowTemplates?: ILumaBotFlowTemplate[];
  flowId?: string;
  loading?: boolean;
  typing?: boolean;
  currentFlowId: string;
  currentFlowTemplateId: string;
  setCurrentAvailability: (value: Record<string, unknown>) => void;
  currentAvailabilitiesList: Record<string, unknown>;
  currentItem?: ILumaBotFlowQuestion;
  messages: TMessageWrapper[];
  isLiveChatEnabled?: boolean;
  shouldDisableHeaderMenuActions?: boolean;
  mode: 'default' | 'blocked';
  inputRef: MutableRefObject<HTMLElement | null>;
  variant?: 'backend';
  isPopper?: boolean;
  setMode: (mode: 'default' | 'blocked') => void;
  pushFlowTemplate: (flowType: ELumaBotFlowType, text?: string) => void;
  pushAnswer: (message: Partial<TMessageWrapper>, opts?: { skipNextItem?: boolean; customDelay?: number }) => void;
  revertMessage: (message: TMessageWrapper) => void;
  clear: () => void;
}

export const chatContextDefaultValue: LumaBotContextProps = {
  settings: {},
  flowTemplates: [],
  loading: false,
  typing: false,
  currentFlowId: '',
  currentFlowTemplateId: '',
  setCurrentAvailability: noop,
  currentAvailabilitiesList: {},
  currentItem: undefined,
  messages: [],
  isLiveChatEnabled: false,
  mode: 'default',
  inputRef: { current: null },
  setMode: noop,
  pushFlowTemplate: noop,
  pushAnswer: noop,
  revertMessage: noop,
  clear: noop,
};

const LumaBotContext = createContext<LumaBotContextProps>(chatContextDefaultValue);

export const LumaBotProvider: React.FC<LumaBotContextProps> = ({ children, ...rest }) => (
  <LumaBotContext.Provider value={rest}>{children}</LumaBotContext.Provider>
);

export const useLumaBotContext = () => {
  const ctx = useContext(LumaBotContext);
  return ctx;
};

if (process.env.NODE_ENV !== 'production') {
  LumaBotContext.displayName = 'LumaLumaBotContext';
}

export default LumaBotContext;
